import type { ZodError } from 'zod'

/**
 * Converts a ZodError into TanStack Form's `{ fields }` error map shape.
 *
 * TanStack Form's Standard Schema adapter requires the schema's input type to
 * match TFormData exactly, which discriminated union schemas don't satisfy.
 * Use this when you need to validate with such a schema manually:
 *
 * ```ts
 * validators: {
 *   onSubmit: ({ value }) => {
 *     const res = myDiscriminatedSchema.safeParse(value)
 *     if (!res.success) return zodErrorToFieldErrors(res.error)
 *   },
 * },
 * ```
 *
 * Each Zod issue path (e.g. `['treatments', 0, 'productId']`) is converted to
 * TanStack Form's dot/bracket notation (`treatments[0].productId`) so errors
 * are distributed to the correct field instances.
 */
export function zodErrorToFieldErrors(error: ZodError): {
  fields: Record<string, { message: string }[]>
} {
  const fields: Record<string, { message: string }[]> = {}

  for (const issue of error.issues) {
    const key = issue.path
      .map((seg, i) =>
        typeof seg === 'number' ? `[${seg}]` : i === 0 ? String(seg) : `.${String(seg)}`
      )
      .join('')
    fields[key] = [...(fields[key] ?? []), { message: issue.message }]
  }

  return { fields }
}
